import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, AlertIcon, Box, Button, FormControl, FormLabel, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  Select, Spinner, Text,
} from '@chakra-ui/react';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { compareTableNames } from '../Table/tableSortUtils';

const api = 'https://api.rcb.tdrsoftware.in/api';
const newRequestKey = () => window.crypto?.randomUUID?.() || `switch-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function SwitchTableModal({ isOpen, onClose, customerId, fromTable, onTransferred }) {
  const [tables, setTables] = useState([]);
  const [toTable, setToTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const inFlight = useRef(false);
  const selectRef = useRef(null);
  const cacheKey = `rcb-table-switch:${customerId}:${fromTable}`;

  useEffect(() => {
    if (!isOpen || !customerId || !fromTable) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setToTable('');
    let saved;
    try { saved = JSON.parse(sessionStorage.getItem(cacheKey)); } catch { saved = null; }
    if (saved?.fromTable !== fromTable) saved = null;
    setPending(saved);
    if (saved) setToTable(saved.toTable);
    axios.get(`${api}/availableTables/${encodeURIComponent(customerId)}`, {
      params: { fromTable }, headers: { Authorization: `Bearer ${Cookies.get('token') || ''}` },
    }).then(({ data }) => {
      if (cancelled) return;
      setTables((data.tables || []).filter(table => table.tableNo !== fromTable)
        .sort((a, b) => compareTableNames(a.tableNo, b.tableNo)));
      if (data.pendingTransfer) {
        setPending(data.pendingTransfer);
        setToTable(data.pendingTransfer.toTable);
      }
    }).catch(err => {
      if (!cancelled) setError(err.response?.data?.message || 'Could not load available tables. Close and try again.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, customerId, fromTable, cacheKey]);

  const transfer = async () => {
    const request = pending || { fromTable, toTable, idempotencyKey: newRequestKey() };
    if (inFlight.current || !request.toTable || request.toTable === request.fromTable) return;
    inFlight.current = true;
    setSubmitting(true);
    setError('');
    setPending(request);
    try { sessionStorage.setItem(cacheKey, JSON.stringify(request)); } catch { /* server journal also supports recovery */ }
    let result;
    try {
      const response = await axios.post(`${api}/switchTable/${encodeURIComponent(customerId)}`, request, {
        headers: { Authorization: `Bearer ${Cookies.get('token') || ''}` },
      });
      result = response.data;
    } catch (err) {
      // Retain the request key on uncertain failures; never issue a second move.
      const rejectedBeforeWrite = [400, 401, 403, 404, 409, 422].includes(err.response?.status)
        && !err.response?.data?.pendingTransfer;
      if (rejectedBeforeWrite) {
        setPending(null);
        try { sessionStorage.removeItem(cacheKey); } catch { /* optional cache */ }
      }
      setError(err.response?.data?.message || 'Transfer status could not be confirmed. Retry the same transfer to safely check or finish it.');
      inFlight.current = false;
      setSubmitting(false);
      return;
    }
    try { sessionStorage.removeItem(cacheKey); } catch { /* optional cache */ }
    setPending(null);
    try {
      await onTransferred(result);
      toast.success(`All items moved from ${result.fromTable} to ${result.toTable}.`);
    } catch {
      toast.success(`Transfer completed to ${result.toTable}. Reopen that table to refresh the display.`);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} initialFocusRef={selectRef}
      closeOnOverlayClick={!submitting} closeOnEsc={!submitting} isCentered size="md">
      <ModalOverlay />
      <ModalContent mx={3} maxH="90dvh">
        <ModalHeader>Switch Table</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody overflowY="auto">
          <Text mb={4}>Move all current items from <strong>{pending?.fromTable || fromTable}</strong> to an available table.</Text>
          {loading ? <Box role="status" mb={3}><Spinner size="sm" mr={2} />Checking available tables…</Box> : null}
          <FormControl>
            <FormLabel htmlFor="switch-table-destination">Available table</FormLabel>
            <Select id="switch-table-destination" ref={selectRef} value={toTable}
              isDisabled={loading || submitting || Boolean(pending)}
              onChange={event => setToTable(event.target.value)}>
              <option value="">Select a table</option>
              {pending && !tables.some(table => table.tableNo === pending.toTable)
                ? <option value={pending.toTable}>{pending.toTable} — pending transfer</option> : null}
              {tables.map(table => <option key={table.tableNo} value={table.tableNo}>{table.tableNo}{table.ac ? ` (${table.ac})` : ''}</option>)}
            </Select>
          </FormControl>
          {!loading && !error && !pending && tables.length === 0
            ? <Text mt={3}>No tables are available right now.</Text> : null}
          <Text mt={3} fontSize="sm" color="gray.600">Quantities, prices, item discounts and notes stay unchanged. Saved invoices and past KOT records are not changed.</Text>
          {pending && !submitting ? <Text mt={3} fontSize="sm">An earlier transfer needs confirmation. Retry to check or finish the same transfer.</Text> : null}
          {error ? <Alert status="error" mt={3} borderRadius="md"><AlertIcon /><Text>{error}</Text></Alert> : null}
        </ModalBody>
        <ModalFooter gap={2} flexWrap="wrap">
          <Button onClick={onClose} isDisabled={submitting}>Cancel</Button>
          <Button colorScheme="blue" onClick={transfer} isLoading={submitting}
            loadingText="Moving items" isDisabled={loading || !toTable || (!pending && !tables.some(table => table.tableNo === toTable))}>
            {pending ? 'Retry transfer' : 'Move all items'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
